import { useMemo } from 'react'
import { Maybe } from '@graphql-tools/utils'
import { Asset, createAsset, SmpteController, SmpteTimestamp, UpNextConfig } from '@/organisms/AngelPlayer'
import { EngagementDetail } from '@/organisms/CollectiblesPlayer/CollectiblesPlayerEngagement/CollectiblesPlayerEngagementContext'
import { GetEpisodeResponse } from '@/services/ProjectsService'
import {
  ContentEpisode,
  Episode,
  NextContentEpisodeSpecialOrMovie,
  WatchableAvailabilityContent,
} from '@/services/ProjectsService/types'
import { GetEpisodeByGuidQuery } from '@/types/codegen-federation'
import { buildLinkToEpisode } from '@/utils/EpisodeUtil'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'

export interface UseEpisodeAssetConfig {
  momentsEnabled?: boolean
  defaultEngagementDetail?: Maybe<EngagementDetail>
}

export const usePureEpisodeAsset = (data?: GetEpisodeByGuidQuery): { asset?: Asset } => {
  const { query } = useSafeRouter()

  return useMemo(() => {
    const episode = data?.episode
    if (!episode) return { asset: undefined }

    const smpte = query.smpte as SmpteTimestamp
    const startTime = query?.position ? parseInt(query.position as string) : 0
    const position = smpte ?? startTime ?? 0

    const asset = createAsset({
      guid: episode.guid as string,
      projectSlug: episode.projectSlug as string,
      slug: episode.slug as string,
      name: episode?.name as string,
      url: episode.source?.url as string,
      title: episode.subtitle as string,
      subtitle: episode.subtitle as string,
      description: episode.description as string,
      position,
      episodeNumber: episode.episodeNumber as number,
      releaseDate: episode.releaseDate,
      seasonId: episode.seasonId as string,
      duration: episode.source?.duration as number,
      videoType: 'episode',
      credits: episode.source?.credits as number,
    })

    return {
      asset,
    }
  }, [data?.episode, query])
}

const getPositionBasedOnWatchPositionAndCredits = (episode: GetEpisodeResponse['episode']) => {
  // if the user hasn't watched before, start at the beginning
  if (!episode?.watchPosition?.position) return 0

  // if the content doesn't have a credits marker, throw up our hands and start where they left off
  if (!episode?.source?.credits) return episode?.watchPosition?.position

  const lastWatchPositionWasBeyondCredits = episode?.watchPosition?.position > episode?.source?.credits
  if (lastWatchPositionWasBeyondCredits) {
    // the user is probably rewatching, lets start at the beginning
    return 0
  } else {
    return episode?.watchPosition?.position
  }
}

const createUpNextMetadata = (
  nextContent: NextContentEpisodeSpecialOrMovie,
  project: GetEpisodeResponse['project'],
  credits: number,
  duration: number,
  navigateToEpisode: (episode: {
    projectSlug: string
    guid: string
    seasonNumber: number | null
    episodeNumber: number | null
    subtitle: string | null
  }) => void,
): UpNextConfig | undefined => {
  if (!nextContent?.id || !project?.slug) {
    return undefined
  }

  return {
    goToNext: () => {
      const episode = {
        projectSlug:
          'project' in nextContent && 'slug' in nextContent.project && nextContent?.project?.slug
            ? nextContent.project.slug
            : project.slug,
        guid: nextContent.id,
        seasonNumber:
          'season' in nextContent && nextContent?.season?.seasonNumber ? nextContent.season.seasonNumber : null,
        episodeNumber: 'episodeNumber' in nextContent && nextContent?.episodeNumber ? nextContent.episodeNumber : null,
        subtitle: 'subtitle' in nextContent && nextContent?.subtitle ? nextContent.subtitle : null,
      }
      navigateToEpisode(episode)
    },
    showTime: Math.max(0, Math.min(credits ? credits + 8 : Number.POSITIVE_INFINITY, duration - 15)),
  }
}

export const useEpisodeAsset = (
  episodeResponse?: GetEpisodeResponse,
  config?: UseEpisodeAssetConfig,
  userEnabledParentalControls?: boolean,
  controller?: SmpteController,
  contentEpisode?: ContentEpisode,
  nextContent?: NextContentEpisodeSpecialOrMovie | null,
  watchableContent?: WatchableAvailabilityContent,
): { asset?: Asset } => {
  const { push, query } = useSafeRouter()
  const { episode, project } = episodeResponse || {}

  const asset = useMemo(() => {
    if (!episode || !project) return

    const credits = contentEpisode?.credits ?? episode.source?.credits ?? 0
    const duration = contentEpisode?.duration ?? episode?.source?.duration ?? 0

    const upNextMetadata = nextContent
      ? createUpNextMetadata(nextContent, project, credits, duration, (ep) =>
          push(buildLinkToEpisode(ep, project.slug)),
        )
      : undefined

    const { episodeSourceUrl, description, releaseDate, vmapUrl, slug, name, guid } = resolveEpisodeFields({
      episode,
      contentEpisode,
      watchableContent,
      userEnabledParentalControls,
    })
    const smpte = query.smpte as SmpteTimestamp
    const startTime = query.position ? parseInt(query.position as string) : undefined
    const controllerTime = controller?.getCurrentTime()

    const position =
      smpte ??
      startTime ??
      controllerTime ??
      config?.defaultEngagementDetail?.startTime ??
      getPositionBasedOnWatchPositionAndCredits(episode)

    return createAsset({
      guid,
      publisherName: project.publisherName,
      projectSlug: project.slug,
      projectName: project.name,
      slug,
      name,
      url: episodeSourceUrl,
      title: episode.subtitle,
      subtitle: episode.subtitle,
      description,
      position,
      upNext: upNextMetadata,
      episodeNumber: episode.episodeNumber,
      releaseDate,
      seasonId: episode.seasonId,
      duration,
      videoType: 'episode',
      credits,
      vmapUrl,
    })
  }, [
    episode,
    project,
    contentEpisode,
    nextContent,
    watchableContent,
    userEnabledParentalControls,
    query.smpte,
    query.position,
    controller,
    config?.defaultEngagementDetail?.startTime,
    push,
  ])

  return { asset }
}

export interface ResolveEpisodeFieldsArgs {
  episode: Episode
  contentEpisode?: ContentEpisode
  userEnabledParentalControls?: boolean
  watchableContent?: WatchableAvailabilityContent
}

export interface ResolvedEpisodeFields {
  episodeSourceUrl: string
  description: string
  releaseDate?: string
  vmapUrl?: string
  slug: string
  name: string
  guid: string
}

export function resolveEpisodeFields({
  episode,
  contentEpisode,
  userEnabledParentalControls,
  watchableContent,
}: ResolveEpisodeFieldsArgs): ResolvedEpisodeFields {
  const skipsUrl = contentEpisode?.skipsUrl ?? episode.source?.skipsUrl
  const episodeUrl = contentEpisode?.url ?? episode.source?.url
  const episodeSourceUrl = (userEnabledParentalControls && skipsUrl ? skipsUrl : episodeUrl) ?? ''
  const description = contentEpisode?.description ?? episode.description ?? ''
  const releaseDate = contentEpisode?.originalReleaseDate ?? episode.releaseDate
  const vmapUrl =
    watchableContent && 'vmapUrl' in watchableContent
      ? watchableContent?.vmapUrl
      : episode.vmapUrl ?? contentEpisode?.vmapUrl
  const slug = contentEpisode?.slug ?? episode.slug ?? ''
  const name = contentEpisode?.name ?? episode.name ?? ''
  const guid = contentEpisode?.id ?? episode.guid ?? ''

  return {
    episodeSourceUrl,
    description,
    releaseDate,
    vmapUrl,
    slug,
    name,
    guid,
  }
}
